import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Activity, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

const KickCounterCard = () => {
  const { token } = useAuth();
  const [recent, setRecent] = useState(0);
  const [total, setTotal] = useState(0);
  const [logging, setLogging] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/wellness/pregnancy/kicks/today`, { headers, withCredentials: true });
      setRecent(res.data.kicks_last_2h || 0);
      setTotal(res.data.total_today || 0);
    } catch {
      /* silent */
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [token]);

  const logKick = async () => {
    setLogging(true);
    try {
      const res = await axios.post(`${API}/wellness/pregnancy/kick`, {}, { headers, withCredentials: true });
      setRecent(res.data.kicks_last_2h);
      setTotal(t => t + 1);
      if (res.data.on_track) {
        toast.success("10 kicks in 2 hours — great movement today 💕");
      }
    } catch {
      toast.error("Couldn't log kick");
    } finally {
      setLogging(false);
    }
  };

  const pct = Math.min(100, (recent / 10) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.24 }}
      data-testid="kick-counter-card"
    >
      <Card className="glass border-rose-400/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-rose-300" />
            Kick Counter
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="text-rose-200 text-xs uppercase tracking-wider mb-1">Last 2 hours</p>
              <p className="text-white text-4xl font-bold" data-testid="kicks-recent">
                {recent} <span className="text-lg text-mystic-300">/ 10</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-rose-200 text-xs uppercase tracking-wider mb-1">Today</p>
              <p className="text-white text-xl font-semibold" data-testid="kicks-today">{total}</p>
            </div>
          </div>
          <Progress value={pct} className="h-2" />
          <Button
            onClick={logKick}
            disabled={logging}
            data-testid="log-kick-btn"
            className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-400 hover:to-pink-400 text-white rounded-full py-6 text-base"
          >
            <Heart className="w-5 h-5 mr-2" />
            {logging ? "Logging..." : "I felt a kick"}
          </Button>
          <p className="text-mystic-200/60 text-xs text-center">
            Healthy benchmark: 10 kicks in 2 hours during active periods.
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default KickCounterCard;
